import { Component, OnInit, OnDestroy, inject, signal, effect, DestroyRef } from '@angular/core';
import { trigger, style, transition, animate, query, stagger } from '@angular/animations';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { StakingService, StakeInfo, PoolUserData } from '../../services/staking.service';
import { Web3Service } from '../../services/web3.service';
import { ModalService } from '../../services/modal.service';
import { ToastService } from '../../services/toast.service';
import { formatUnits, parseUnits } from 'viem';
import { untracked } from '@angular/core';
import { interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PoolTab } from '../../models/pool-tab.enum';

export interface PoolExplorerData extends PoolUserData {
  id: number;
  maturityPeriod: number;
  apy: number;
  totalStaked: string;
  isActive: boolean;
  participantCount: number;
  stakingEndTime: number;   // ← added
  maxStakingAmount: string; 
  isExpanded?: boolean;
}

@Component({
  selector: 'app-pool-explorer',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DatePipe],
  templateUrl: './pool-explorer.component.html',
  styleUrl: './pool-explorer.component.css',
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden', transform: 'translateY(-10px)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden', transform: 'translateY(0)' }),
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ]),
    trigger('stakeList', [
      transition(':enter', [
        query('.stake-row', [
          style({ opacity: 0, transform: 'translateY(-6px)' }),
          stagger(60, animate('220ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0)' })))
        ], { optional: true })
      ]),
      transition(':leave', [
        query('.stake-row', [
          stagger(-40, animate('160ms ease-in', style({ opacity: 0, transform: 'translateY(-4px)' })))
        ], { optional: true })
      ])
    ])
  ]
})
export class PoolExplorerComponent implements OnInit, OnDestroy {
  stakingService = inject(StakingService);
  web3Service    = inject(Web3Service);
  modalService   = inject(ModalService);
  toastService   = inject(ToastService);
  destroyRef     = inject(DestroyRef);

  PoolTab        = PoolTab; // Expose to template
  pools          = signal<PoolExplorerData[]>([]);
  isLoading      = signal<boolean>(true);
  expandedPoolId = signal<number | null>(null);
  activeTab      = signal<PoolTab>(PoolTab.LIVE);
  stakedPoolIds  = signal<number[]>([]);
  isPending      = signal<boolean>(false);
  searchQuery    = signal<string>('');

  // Modal state
  showStakeModal              = signal<boolean>(false);
  showWithdrawModal           = signal<boolean>(false);
  modalPoolId                 = signal<number | null>(null);
  stakeInputAmount            = signal<string>('');
  selectedStakeForWithdraw    = signal<StakeInfo | null>(null);
  userBalance                 = signal<string>('0');

  constructor() {
    effect(() => {
      const addr = this.web3Service.userAddress();
      if (addr && !untracked(() => this.isLoading())) {
        this.loadPoolsWithUserData();
      } else if (!addr) {
        this.clearUserData();
      }
    });
  }

  ngOnInit() {
    this.loadPoolsWithUserData();
    this.fetchUserBalance();

    // Refresh pool data every 30s
    interval(30000).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.web3Service.userAddress() && this.pools().length > 0) {
        this.refreshPoolsData();
      }
    });

    // Tick countdowns every 1s
    interval(1000).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.decrementCountdowns();
    });
  }

  ngOnDestroy() {
    // takeUntilDestroyed handles cleanup
  }

  // ─── Data Loading ─────────────────────────────────────────────────────────

  async loadPoolsWithUserData() {
    if (this.isLoading() && this.pools().length > 0) return;
    this.isLoading.set(true);

    try {
      const poolCountObj = await this.stakingService.readStakingContract('poolCount');
      if (poolCountObj === undefined || poolCountObj === null) return;

      const count       = Number(poolCountObj);
      const userAddress = this.web3Service.userAddress();

      // 1. Fetch all pool info in one multicall
      const poolCalls   = Array.from({ length: count }, (_, i) => ({
        functionName: 'getPoolInfo', args: [BigInt(i)]
      }));
      const poolResults = await this.stakingService.multicallStaking(poolCalls);

      // 2. Fetch user data per pool (parallel)
      const userDataMap = new Map<number, PoolUserData>();
      if (userAddress) {
        try {
          const stakedPoolsRes = await this.stakingService.readStakingContract('getUserStakedPools', [userAddress]);
          if (stakedPoolsRes && Array.isArray(stakedPoolsRes)) {
            this.stakedPoolIds.set(stakedPoolsRes.map(id => Number(id)));
          }
        } catch (e) {
          console.error('Error fetching staked pools', e);
        }

        await Promise.all(
          Array.from({ length: count }, (_, i) =>
            this.stakingService
              .getUserPoolAggregates(userAddress as `0x${string}`, BigInt(i))
              .then(data => userDataMap.set(i, data))
              .catch(() => {})
          )
        );
      }

      const loadedPools: PoolExplorerData[] = [];
      for (let i = 0; i < count; i++) {
        const p = poolResults[i]?.result;
        if (!p) continue;

        const userData = userDataMap.get(i) ?? {
          totalUserStaked: '0',
          totalUserRewards: '0',
          userPoolExpiry: 0,
          userStakeCount: 0,
          userStakes: []
        };

        loadedPools.push({
          id:               i,
          maturityPeriod:   Number(p[0] || 0),
          apy:              Number(p[1] || 0),
          totalStaked:      formatUnits(p[2] || 0n, 18),
          isActive:         !!p[7],
          participantCount: Number(p[8] || 0),
          stakingEndTime:   Number(p[10] || 0),   // ← populate from contract
          maxStakingAmount: formatUnits(p[11] || 0n, 18),
          ...userData,
          isExpanded: this.expandedPoolId() === i
        });
      }

      this.pools.set(loadedPools);
    } catch (e) {
      console.error('Failed to load pools', e);
    } finally {
      this.isLoading.set(false);
    }
  }

  async refreshPoolsData() {
    const userAddress = this.web3Service.userAddress();
    if (!userAddress) return;
    try {
      const userDatas = await Promise.all(
        this.pools().map(pool =>
          this.stakingService.getUserPoolAggregates(userAddress as `0x${string}`, BigInt(pool.id))
        )
      );
      this.pools.update(pools => pools.map((pool, j) => ({ ...pool, ...userDatas[j] })));
    } catch (e) {
      console.error('Error refreshing pools data', e);
    }
  }

  private decrementCountdowns() {
    this.pools.update(poolsList =>
      poolsList.map(pool => ({
        ...pool,
        userStakes: pool.userStakes.map(stake => {
          if (!stake.isMatured && stake.remainingSeconds > 0) {
            const newSeconds = stake.remainingSeconds - 1;
            return {
              ...stake,
              remainingSeconds: newSeconds,
              formattedCountdown: this.formatTime(newSeconds),
              isMatured: newSeconds <= 0
            };
          }
          return stake;
        })
      }))
    );
  }

  private formatTime(totalSeconds: number): string {
    if (totalSeconds <= 0) return 'Matured';
    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);

    let t = '';
    if (d > 0) t += `${d}d `;
    if (h > 0 || d > 0) t += `${h.toString().padStart(2, '0')}h `;
    if (m > 0 || h > 0 || d > 0) t += `${m.toString().padStart(2, '0')}m `;
    t += `${s.toString().padStart(2, '0')}s`;
    return t.trim();
  }

  clearUserData() {
    this.stakedPoolIds.set([]);
    this.pools.update(p =>
      p.map(pool => ({
        ...pool,
        totalUserStaked:  '0',
        totalUserRewards: '0',
        userPoolExpiry:    0,
        userStakeCount:    0,
        userStakes:        []
      }))
    );
  }

  // ─── UI Helpers ────────────────────────────────────────────────────────────

  togglePoolExpand(poolId: number) {
    const target = this.expandedPoolId() === poolId ? null : poolId;
    this.expandedPoolId.set(target);
    this.pools.update(p =>
      p.map(pool => ({ ...pool, isExpanded: pool.id === target }))
    );
  }

  getFilteredPools(): PoolExplorerData[] {
    const tab   = this.activeTab();
    const query = this.searchQuery().toLowerCase();
    return this.pools().filter(p => {
      const isStakingEnded = this.isStakingEnded(p);
      let matchesTab = false;
      
      if (tab === PoolTab.LIVE) {
        matchesTab = p.isActive && !isStakingEnded;
      } else if (tab === PoolTab.COMPLETED) {
        matchesTab = !p.isActive || isStakingEnded;
      } else if (tab === PoolTab.STAKED) {
        matchesTab = this.stakedPoolIds().includes(p.id);
      }

      const matchesSearch = query === '' ||
        p.maturityPeriod.toString().includes(query) ||
        (p.maturityPeriod / 86400).toString().includes(query) ||
        'months'.includes(query) || 'pool'.includes(query);
      return matchesTab && matchesSearch;
    });
  }

  /** Returns true when the staking window has passed (users can no longer stake but can still earn/withdraw) */
  isStakingEnded(pool: PoolExplorerData | null): boolean {
    if (!pool || !pool.stakingEndTime || pool.stakingEndTime === 0) return false;
    return Date.now() / 1000 > pool.stakingEndTime;
  }

  getPoolById(id: number): PoolExplorerData | null {
    return this.pools().find(p => p.id === id) || null;
  }

  /** Net amount user receives after early-withdrawal penalty */
  getNetWithdrawAmount(stake: StakeInfo): string {
    const amount  = Number(stake.amount);
    const penalty = (amount * stake.penaltyPercent) / 100;
    return (amount - penalty).toFixed(4);
  }

  /** Total amount user receives: net principal + rewards */
  getTotalWithdrawAmount(stake: StakeInfo): string {
    const netPrincipal = Number(this.getNetWithdrawAmount(stake));
    const rewards      = Number(stake.pendingRewards);
    return (netPrincipal + rewards).toFixed(4);
  }

  getDays(seconds: number): number   { return Math.floor(seconds / 86400); }
  getMonths(seconds: number): number { return Math.round(this.getDays(seconds) / 30); }
  toNumber(val: string): number      { return Number(val); }

  // ─── Balance ───────────────────────────────────────────────────────────────

  async fetchUserBalance() {
    const addr = this.web3Service.userAddress();
    if (!addr) return;
    try {
      const config = this.web3Service.getWagmiConfig();
      const { readContract } = await import('@wagmi/core');
      const { TOKEN_ABI, CONTRACT_ADDRESSES } = await import('../../constants/contracts');
      const bal = await readContract(config, {
        address:      CONTRACT_ADDRESSES.LOONY_TOKEN,
        abi:          TOKEN_ABI,
        functionName: 'balanceOf',
        args:         [addr as `0x${string}`]
      });
      this.userBalance.set(formatUnits(bal as bigint, 18));
    } catch (e) {}
  }

  // ─── Modal Handlers ────────────────────────────────────────────────────────

  openStakeModal(poolId: number) {
    this.modalPoolId.set(poolId);
    this.stakeInputAmount.set('');
    this.showStakeModal.set(true);
    this.fetchUserBalance();
  }

  setMaxStake() {
    this.stakeInputAmount.set(this.userBalance());
  }

  async handleStake() {
    const amount = this.stakeInputAmount();
    const poolId = this.modalPoolId();
    if (!amount || Number(amount) <= 0 || poolId === null) return;

    const pool = this.getPoolById(poolId);
    if (this.isStakingEnded(pool)) {
      this.toastService.error('Staking has ended for this pool');
      return;
    }

    this.isPending.set(true);
    try {
      await this.stakingService.doStake(BigInt(poolId), parseUnits(amount.toString(), 18));
      this.toastService.success('Tokens staked successfully!');
      this.showStakeModal.set(false);
      this.stakeInputAmount.set('');
      await this.loadPoolsWithUserData();
    } catch (e: any) {
      console.error('Staking error:', e);
      // Try to extract a more useful message if possible
      const msg = e.reason || e.message || 'Staking failed';
      this.toastService.error(msg);
    } finally {
      this.isPending.set(false);
    }
  }

  openWithdrawModal(poolId: number, stake: StakeInfo) {
    this.modalPoolId.set(poolId);
    this.selectedStakeForWithdraw.set(stake);
    this.showWithdrawModal.set(true);
  }

  async handleWithdraw() {
    const poolId = this.modalPoolId();
    const stake  = this.selectedStakeForWithdraw();
    if (poolId === null || !stake) return;

    this.isPending.set(true);
    try {
      await this.stakingService.writeStakingContract('unstake', [BigInt(poolId), BigInt(stake.index)]);
      this.toastService.success('Tokens withdrawn successfully!');
      this.showWithdrawModal.set(false);
      await this.loadPoolsWithUserData();
    } catch (e) {
      this.toastService.error('Withdrawal failed');
    } finally {
      this.isPending.set(false);
    }
  }

  async handleClaim(poolId: number, stakeIndex: number) {
    this.isPending.set(true);
    try {
      await this.stakingService.writeStakingContract('claimRewards', [BigInt(poolId), BigInt(stakeIndex)]);
      this.toastService.success('Rewards claimed!');
      await this.loadPoolsWithUserData();
    } catch (e) {
      this.toastService.error('Claim failed');
    } finally {
      this.isPending.set(false);
    }
  }
}
